import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Card, Col, Form, Input, InputNumber, Row, Select, Space, Spin, Typography, message } from "antd";
import { Controller, useForm, type Control } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useEffect } from "react";
import { z } from "zod";
import type { FuelType } from "@autoflow/types";
import { useAppConfig } from "../../shared/config/AppConfigProvider";
import { formatGroupedInput, formatPhone, normalizeVinInput, parseGroupedInput } from "../../shared/utils/formatters";
import { customersApi } from "../customers/customers-api";
import { customerName } from "../customers/customer-types";
import { FieldScanButton } from "../scan/FieldScanButton";
import { vehiclesApi, type VehicleInput } from "./vehicles-api";

const vehicleSchema = z.object({
  customerId: z.string().uuid(),
  vin: z.string().length(17).regex(/^[A-HJ-NPR-Z0-9]{17}$/i),
  licensePlate: z.string().optional(),
  year: z.number().int().min(1886).max(2100),
  make: z.string().min(1),
  model: z.string().min(1),
  engine: z.string().optional(),
  transmission: z.string().optional(),
  mileage: z.number().int().min(0).optional(),
  color: z.string().optional(),
  fuelType: z.enum(["GASOLINE", "DIESEL", "HYBRID", "ELECTRIC", "FLEX_FUEL", "OTHER"]).optional(),
  notes: z.string().optional()
});
const fuels: FuelType[] = ["GASOLINE", "DIESEL", "HYBRID", "ELECTRIC", "FLEX_FUEL", "OTHER"];

export function VehicleFormPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const editing = Boolean(id);
  const { text } = useAppConfig();
  const navigate = useNavigate();
  const client = useQueryClient();
  const [messageApi, contextHolder] = message.useMessage();
  const vehicle = useQuery({ queryKey: ["vehicle", id], queryFn: () => vehiclesApi.get(id!), enabled: editing });
  const customers = useQuery({ queryKey: ["customers", "vehicle-form"], queryFn: () => customersApi.list("", 1) });
  const { control, handleSubmit, watch, setValue, formState: { errors } } = useForm<VehicleInput>({
    resolver: zodResolver(vehicleSchema),
    defaultValues: { customerId: params.get("customerId") ?? "", vin: "", year: new Date().getFullYear(), make: "", model: "" },
    values: vehicle.data ? {
      customerId: vehicle.data.customerId, vin: vehicle.data.vin, licensePlate: vehicle.data.licensePlate ?? "",
      year: vehicle.data.year, make: vehicle.data.make, model: vehicle.data.model, engine: vehicle.data.engine ?? "",
      transmission: vehicle.data.transmission ?? "", mileage: vehicle.data.mileage ?? undefined,
      color: vehicle.data.color ?? "", fuelType: vehicle.data.fuelType ?? undefined, notes: vehicle.data.notes ?? ""
    } : undefined
  });
  const vin = watch("vin");
  const vinDecode = useQuery({
    queryKey: ["vin-decode", vin],
    queryFn: () => vehiclesApi.decodeVin(vin),
    enabled: !editing && /^[A-HJ-NPR-Z0-9]{17}$/.test(vin),
    staleTime: 24 * 60 * 60 * 1_000
  });
  useEffect(() => {
    if (!vinDecode.data) return;
    if (vinDecode.data.year) setValue("year", vinDecode.data.year, { shouldValidate: true });
    if (vinDecode.data.make) setValue("make", vehicleDisplayCase(vinDecode.data.make), { shouldValidate: true });
    if (vinDecode.data.model) setValue("model", vehicleDisplayCase(vinDecode.data.model), { shouldValidate: true });
    if (vinDecode.data.transmission) setValue("transmission", vinDecode.data.transmission);
    if (vinDecode.data.engineModel) setValue("engine", vinDecode.data.engineModel);
  }, [setValue, vinDecode.data]);
  const mutation = useMutation({
    mutationFn: (value: VehicleInput) => editing ? vehiclesApi.update(id!, value) : vehiclesApi.create(value),
    onSuccess: result => {
      void client.invalidateQueries({ queryKey: ["vehicles"] });
      messageApi.success(text.save);
      navigate(`/vehicles/${result.id}`, { replace: true });
    }
  });
  if (editing && vehicle.isLoading) return <Spin />;
  return <section className="form-page">
    {contextHolder}
    <div className="page-title"><Typography.Title level={2}>{editing ? text.editVehicle : text.newVehicle}</Typography.Title></div>
    {mutation.isError && <Alert type="error" showIcon message={(mutation.error as Error).message} />}
    <form onSubmit={handleSubmit(value => mutation.mutate(value))}>
      <Card title={text.vehicle}>
        <Row gutter={18}>
          <Col xs={24}><Controller name="customerId" control={control} render={({ field }) => <Form.Item label={text.owner} validateStatus={errors.customerId ? "error" : undefined} help={errors.customerId?.message}><Select {...field} showSearch optionFilterProp="label" options={(customers.data?.items ?? []).map(item => ({ value: item.id, label: `${customerName(item)} · ${formatPhone(item.phone)}` }))} /></Form.Item>} /></Col>
          <Col xs={24} md={14}><TextField control={control} name="vin" label={text.vin} error={errors.vin?.message} vin scanMode="vin" /></Col>
          <Col xs={24} md={10}><TextField control={control} name="licensePlate" label={text.plate} uppercase scanMode="plate" /></Col>
          {!editing && (vinDecode.isFetching || vinDecode.data) && <Col xs={24}><Alert
            type={vinDecode.data?.year && vinDecode.data.make && vinDecode.data.model ? "success" : "info"}
            showIcon
            message={vinDecode.isFetching ? text.decodingVin : vinDecode.data?.year && vinDecode.data.make && vinDecode.data.model
              ? `${text.vinDecoded}: ${vinDecode.data.year} ${vehicleDisplayCase(vinDecode.data.make)} ${vehicleDisplayCase(vinDecode.data.model)}`
              : text.vinDecodePartial}
          /></Col>}
          <Col xs={12} md={6}><NumberField control={control} name="year" label={text.year} error={errors.year?.message} /></Col>
          <Col xs={12} md={9}><TextField control={control} name="make" label={text.make} error={errors.make?.message} /></Col>
          <Col xs={24} md={9}><TextField control={control} name="model" label={text.model} error={errors.model?.message} /></Col>
          <Col xs={24} md={12}><TextField control={control} name="engine" label={text.engine} /></Col>
          <Col xs={24} md={12}><TextField control={control} name="transmission" label={text.transmission} /></Col>
          <Col xs={12} md={8}><NumberField control={control} name="mileage" label={text.mileage} grouped /></Col>
          <Col xs={12} md={8}><TextField control={control} name="color" label={text.color} /></Col>
          <Col xs={24} md={8}><Controller name="fuelType" control={control} render={({ field }) => <Form.Item label={text.fuelType}><Select {...field} allowClear options={fuels.map(value => ({ value, label: value.replace("_", " ") }))} /></Form.Item>} /></Col>
          <Col xs={24}><Controller name="notes" control={control} render={({ field }) => <Form.Item label={text.notes}><Input.TextArea {...field} rows={3} /></Form.Item>} /></Col>
        </Row>
      </Card>
      <Space className="form-actions"><Button onClick={() => navigate(-1)}>{text.cancel}</Button><Button type="primary" htmlType="submit" loading={mutation.isPending}>{text.save}</Button></Space>
    </form>
  </section>;
}

function TextField({ control, name, label, error, vin = false, uppercase = false, scanMode }: { control: Control<VehicleInput>; name: keyof VehicleInput; label: string; error?: string; vin?: boolean; uppercase?: boolean; scanMode?: "vin" | "plate" }) {
  return <Controller name={name} control={control} render={({ field }) => <Form.Item label={label} validateStatus={error ? "error" : undefined} help={error}><Input
    {...field}
    className={vin ? "mono" : undefined}
    maxLength={vin ? 17 : undefined}
    autoCapitalize={vin ? "characters" : undefined}
    spellCheck={vin ? false : undefined}
    value={vin ? normalizeVinInput(String(field.value ?? "")) : uppercase ? String(field.value ?? "").toUpperCase() : String(field.value ?? "")}
    onChange={event => field.onChange(vin ? normalizeVinInput(event.target.value) : uppercase ? event.target.value.toUpperCase() : event.target.value)}
    suffix={scanMode ? <FieldScanButton mode={scanMode} onResult={field.onChange} /> : undefined}
  /></Form.Item>} />;
}

function vehicleDisplayCase(value: string): string {
  return value === value.toUpperCase()
    ? value.toLowerCase().replace(/\b\w/g, letter => letter.toUpperCase())
    : value;
}
function NumberField({ control, name, label, error, grouped = false }: { control: Control<VehicleInput>; name: keyof VehicleInput; label: string; error?: string; grouped?: boolean }) {
  return <Controller name={name} control={control} render={({ field }) => <Form.Item label={label} validateStatus={error ? "error" : undefined} help={error}><InputNumber
    {...field}
    value={typeof field.value === "number" ? field.value : undefined}
    min={0}
    precision={0}
    formatter={grouped ? formatGroupedInput : undefined}
    parser={grouped ? parseGroupedInput : undefined}
    style={{ width: "100%" }}
  /></Form.Item>} />;
}
